import React from 'react';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

import type { StudioSwatch } from '../../../domain';
import { resolveAssetUrl } from '../../../utils/assetUrl';

export interface SwatchProps {
  swatch: StudioSwatch;
  /** Palette classifier — picks the visual rendering. "color" -> swatch
   *  is a CSS color; "material" / "texture" / default -> thumbnailUrl
   *  with a label fallback. */
  kind: string;
  active: boolean;
  onSelect: (swatch: StudioSwatch) => void;
  /** When provided and this is a color swatch, an inspect affordance opens
   *  the color-model viewer (anchored to the passed element). */
  onInspect?: (anchor: HTMLElement, swatch: StudioSwatch) => void;
  /**
   * Tile edge length in px. Defaults to `defaultSwatchSize(kind)` — 36 for
   * color palettes (color circles read fine at compact sizes), 56 for
   * thumbnail-driven palettes (Styles, Materials) where the image needs
   * room to breathe. Elevation-pass 2026-05-26 tile-sizing change.
   */
  size?: number;
  /**
   * Render the swatch label as a caption below the tile (small text), turning
   * the swatch into an image card. Default false preserves the bare-tile usage
   * (color circles, compact rows). The full label still shows on hover.
   */
  showLabel?: boolean;
  /**
   * App-origin thumbnail URL (e.g. a bundled `/swatches/...` asset). Used when
   * the backend `swatch.thumbnailUrl` is absent. Passed through as-is — it is
   * already absolute to the FE origin, so it must NOT go through
   * `resolveAssetUrl` (which targets the backend).
   */
  bundledThumbnail?: string;
}

/**
 * Tile size policy keyed off palette kind. Color palettes stay compact
 * (the circle reads at 36px); thumbnail-driven palettes scale up so the
 * image is recognizable. Centralized so SwatchPanel + tests stay aligned.
 */
export function defaultSwatchSize(kind: string): number {
  return kind.toLowerCase() === 'color' ? 36 : 56;
}

/**
 * Single-swatch renderer. The shape adapts to the palette's `kind`:
 * - `color`: filled circle in the swatch's CSS color (the `value`).
 * - Anything else: square with the thumbnailUrl, or the first 2 chars of
 *   the label as a fallback. Hover tooltip always shows the full label.
 *
 * Robustness: a relative `thumbnailUrl` is resolved against the API base, and
 * a load failure falls back to the 2-char label tile (so a broken image URL
 * never leaves a broken-image glyph). The active state is a 2px ring.
 */
export const Swatch: React.FC<SwatchProps> = ({
  swatch,
  kind,
  active,
  onSelect,
  onInspect,
  size,
  showLabel = false,
  bundledThumbnail,
}) => {
  const isColor = kind.toLowerCase() === 'color';
  const tileSize = size ?? defaultSwatchSize(kind);
  const [imgError, setImgError] = React.useState(false);
  // Backend thumbnailUrl wins; fall back to the FE-bundled thumbnail; then the
  // 2-char label tile (handled below via imgError / absence).
  const thumbnail = resolveAssetUrl(swatch.thumbnailUrl) ?? bundledThumbnail;

  // Reset the error flag if the URL changes (e.g. studio switch reuses the node).
  React.useEffect(() => {
    setImgError(false);
  }, [thumbnail]);

  const labelTile = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: 'background.paper',
        border: theme => `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700 }}>
        {swatch.label.slice(0, 2).toUpperCase()}
      </Typography>
    </Box>
  );

  const tile = (
    <Box
      // The dynamic size is applied inline so jsdom tests can assert the
      // computed dims (sx generates a class-based style invisible to
      // `getBoundingClientRect` under jsdom). All static styling stays in sx.
      style={{ width: tileSize, height: tileSize }}
      sx={{
        position: 'relative',
        flexShrink: 0,
        // Reveal the inspect affordance on hover / keyboard focus.
        '&:hover .swatch-inspect, &:focus-within .swatch-inspect': { opacity: 1 },
      }}
    >
      <Tooltip title={swatch.label} arrow placement="top">
        <ButtonBase
          onClick={() => onSelect(swatch)}
          aria-label={swatch.label}
          aria-pressed={active}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: isColor ? '50%' : 1,
            position: 'relative',
            overflow: 'hidden',
            outline: theme =>
              active ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
            outlineOffset: 2,
            transition: 'outline-color 120ms, transform 120ms ease, box-shadow 120ms ease',
            // Tactile hover: lift + lift-shadow. The transform is GPU-cheap and
            // the parent tile reserves the space, so neighbours don't reflow.
            '&:hover': {
              transform: 'scale(1.06)',
              boxShadow: theme => theme.shadows[4],
              zIndex: 1,
            },
            '&:active': { transform: 'scale(1.02)' },
          }}
        >
          {isColor ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                bgcolor: swatch.value,
                border: theme => `1px solid ${theme.palette.divider}`,
              }}
            />
          ) : thumbnail && !imgError ? (
            <Box
              component="img"
              src={thumbnail}
              alt={swatch.label}
              onError={() => setImgError(true)}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            labelTile
          )}
        </ButtonBase>
      </Tooltip>

      {isColor && onInspect && (
        <Tooltip title="Inspect color" arrow>
          {/* component="span" so this isn't a <button> nested in the swatch ButtonBase. */}
          <IconButton
            component="span"
            size="small"
            className="swatch-inspect"
            aria-label={`Inspect ${swatch.label}`}
            onClick={e => {
              e.stopPropagation();
              onInspect(e.currentTarget as unknown as HTMLElement, swatch);
            }}
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              p: 0.25,
              bgcolor: 'background.paper',
              border: theme => `1px solid ${theme.palette.divider}`,
              opacity: 0,
              transition: 'opacity 120ms',
              '&:hover': { opacity: 1, bgcolor: 'background.paper' },
            }}
          >
            <TuneIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  if (!showLabel) return tile;

  // Image-card layout: tile with the label as small text below. The caption
  // clamps to 2 lines; the hover tooltip on the tile still shows the full label.
  return (
    <Box
      style={{ width: tileSize }}
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
    >
      {tile}
      <Typography
        variant="caption"
        align="center"
        sx={{
          width: '100%',
          fontSize: 10.5,
          lineHeight: 1.2,
          color: active ? 'text.primary' : 'text.secondary',
          fontWeight: active ? 600 : 400,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {swatch.label}
      </Typography>
    </Box>
  );
};
